import { IAuthProvider, User, setStoredAuthProvider } from '../AuthProvider'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { clearUserProfile } from '../profileStorage'

const getApiUrl = (route: string): string => {
  const baseUrl = import.meta.env.VITE_API_ENDPOINT
  return `${baseUrl}${route}`
}

const getHeaders = (token: string | null | undefined): { [key: string]: string } => {
  const accessToken = localStorage.getItem('AccessToken')
  const orgId = localStorage.getItem('OrgId')
  const orgUuid = localStorage.getItem('OrgUuid')

  const headers: { [key: string]: string } = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (accessToken) headers['X-Access-Token'] = accessToken
  if (orgId) headers['X-Org-Id'] = orgId
  if (orgUuid) headers['X-Org-Uuid'] = orgUuid
  return headers
}

async function isValidToken(token: string | undefined): Promise<boolean> {
  try {
    const url = getApiUrl('/api/auth/verify')
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })

    await response.json()
    return true
  } catch (error) {
    return false
  }
}

export const pointClickCareAuthProvider = (): IAuthProvider => {
  const STORAGE_KEYS = {
    idToken: 'IdToken',
    accessToken: 'AccessToken',
    orgId: 'OrgId',
    orgUuid: 'OrgUuid'
  } as const

  const removeCookieEverywhere = (key: string) => {
    try {
      // Always try removing with just path first (works for localhost)
      Cookies.remove(key, { path: '/' })

      // Try removing with domain when it's a real domain (not localhost/IP)
      const host = window.location.hostname
      const isRealDomain = /\./.test(host) && !/^\d+\.\d+\.\d+\.\d+$/.test(host)
      if (isRealDomain) {
        Cookies.remove(key, { path: '/', domain: `.${host}` })
      }
    } catch {}
  }

  const clearStorage = (): void => {
    Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k))
    removeCookieEverywhere(STORAGE_KEYS.idToken)
    removeCookieEverywhere(STORAGE_KEYS.accessToken)
    removeCookieEverywhere(STORAGE_KEYS.orgId)
    removeCookieEverywhere(STORAGE_KEYS.orgUuid)
    clearUserProfile()
    setStoredAuthProvider(null)
  }

  return {
    getUser: async () => {
      const cookieToken = Cookies.get(STORAGE_KEYS.idToken)
      let token = localStorage.getItem(STORAGE_KEYS.idToken)

      if (cookieToken && cookieToken !== token) {
        const isValid = await isValidToken(cookieToken)
        if (!isValid) {
          clearStorage()
          return
        }

        const accessTokenCookie = Cookies.get(STORAGE_KEYS.accessToken)
        const orgIdCookie = Cookies.get(STORAGE_KEYS.orgId)
        const orgUuidCookie = Cookies.get(STORAGE_KEYS.orgUuid)

        if (accessTokenCookie) localStorage.setItem(STORAGE_KEYS.accessToken, accessTokenCookie)
        if (orgIdCookie) localStorage.setItem(STORAGE_KEYS.orgId, orgIdCookie)
        if (orgUuidCookie) localStorage.setItem(STORAGE_KEYS.orgUuid, orgUuidCookie)
        localStorage.setItem(STORAGE_KEYS.idToken, (token = cookieToken))
        setStoredAuthProvider('pointClickCare')

        removeCookieEverywhere(STORAGE_KEYS.idToken)
        removeCookieEverywhere(STORAGE_KEYS.accessToken)
        removeCookieEverywhere(STORAGE_KEYS.orgId)
        removeCookieEverywhere(STORAGE_KEYS.orgUuid)
      }

      if (!token) return

      try {
        const decoded = jwtDecode<User>(token)
        // If token has exp and it's expired, clear and exit
        if (decoded?.exp && Date.now() >= decoded.exp * 1000) {
          clearStorage()
          return
        }
        setStoredAuthProvider('pointClickCare')
        return decoded
      } catch {
        // Malformed token
        clearStorage()
        return
      }
    },
    login: () => {
      setStoredAuthProvider('pointClickCare')
      const baseUrl = (import.meta.env.VITE_API_ENDPOINT || '').replace(/\/$/, '')
      window.location.assign(`${baseUrl}/api/authprovider?code=pcc`)
    },
    logout: () => {
      clearStorage()
    },
    getToken: () => {
      return localStorage.getItem(STORAGE_KEYS.idToken)
    },
    getHeaders: () => {
      const token = localStorage.getItem(STORAGE_KEYS.idToken)
      return getHeaders(token)
    }
  }
}
